import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Button, Div, Dropdown, Flex, TagsTextArea } from 'ui';

export const TextAreaWrapper = styled(Div)`
  margin-bottom: 20px;
`;
export const StyledLabel = styled.p`
  font-size: 13px;
  margin-bottom: 5px;
`;

export const StyledDropdown = styled(Dropdown)`
  width: 60%;
`;

export const StyledTranslateButton = styled(Button)`
  text-transform: uppercase;
  background-color: ${p => p.theme.colors.theme} !important;
`;

const OriginalSection = ({
  intl,
  isLoading,
  detectLanguage,
  translatorLanguages,
  translateFromLanguage,
  setTranslateFromLanguage,
  setOriginalTextValue,
  originalTextValue,
  isDetecting,
  detectedLanguage,
  visible,
  locale,
  isDisabled,
  tags,
}) => {
  const [isManuallySelected, setIsManuallySelected] = useState(false);
  const [prevDetectedLanguage, setPrevDetectedLanguage] = useState();

  const addDetectedLabel = () => {
    if (
      translatorLanguages?.length &&
      detectedLanguage?.language &&
      detectedLanguage?.isTranslationSupported
    ) {
      const usedLanguage = translatorLanguages?.find(l => l.key === detectedLanguage.language);
      const prevLanguage = translatorLanguages?.find(l => l.key === prevDetectedLanguage);
      if (prevLanguage) {
        prevLanguage.name = prevLanguage.name.replace(/(.*) \(.*?\)/gm, '$1');
      }

      if (usedLanguage) {
        usedLanguage.name = `${usedLanguage.name} (${intl.formatMessage({
          id: 'translation.DETECTED',
        })})`;
        setTranslateFromLanguage(usedLanguage.key);
      }
    }
  };

  useEffect(() => {
    if (visible) {
      if (originalTextValue) {
        detectLanguage({
          text: originalTextValue,
        });
      }
      setIsManuallySelected(false);
    } else {
      // Remove detected label
      if (prevDetectedLanguage) {
        const prevLanguage = translatorLanguages?.find(l => l.key === prevDetectedLanguage);
        if (prevLanguage) {
          prevLanguage.name = prevLanguage.name.replace(/(.*) \(.*?\)/gm, '$1');
        }
      }
      setPrevDetectedLanguage();
      setTranslateFromLanguage('en');
    }
  }, [visible]);

  useEffect(() => {
    if (translatorLanguages?.length) {
      setTranslateFromLanguage(
        translatorLanguages?.find(l => l.key === locale.split('-')[0])?.key || 'en',
      );
      addDetectedLabel();
    }
  }, [translatorLanguages]);

  useEffect(() => {
    if (detectedLanguage.language !== prevDetectedLanguage) {
      setPrevDetectedLanguage(detectedLanguage.language);
      addDetectedLabel();
    }
  }, [detectedLanguage]);

  const debouncedDetect = useCallback(
    debounce((text) => {
      detectLanguage({
        text,
      });
    }, 400),
    [],
  );

  const updateOriginalText = (e) => {
    const text = e.target.value;
    if (!isManuallySelected && text) {
      debouncedDetect(text);
    }
    setOriginalTextValue(text);
  };

  const updateTranslatedFrom = (e) => {
    setTranslateFromLanguage(e);
    setIsManuallySelected(true);
  };

  return (
    <>
      <StyledLabel>{intl.formatMessage({ id: 'translation.ORIGINAL_TEXT' })}</StyledLabel>
      <TextAreaWrapper>
        <TagsTextArea
          height='150px'
          tags={tags}
          value={originalTextValue}
          onChange={updateOriginalText}
          disabled={isLoading || isDisabled}
        />
      </TextAreaWrapper>
      <StyledLabel>{intl.formatMessage({ id: 'translation.TRANSLATE_FROM' })}</StyledLabel>
      <Flex jcsb>
        <StyledDropdown
          placeholder={'language'}
          values={translatorLanguages}
          valueKey='key'
          valueLabel='name'
          disabled={isLoading || isDetecting}
          value={translateFromLanguage}
          onChange={e => updateTranslatedFrom(e)}
        />
      </Flex>
    </>
  );
};

OriginalSection.propTypes = {
  intl: PropTypes.object,
  isLoading: PropTypes.bool,
  detectLanguage: PropTypes.func,
  translatorLanguages: PropTypes.array,
  translateFromLanguage: PropTypes.string,
  setTranslateFromLanguage: PropTypes.func,
  setOriginalTextValue: PropTypes.func,
  originalTextValue: PropTypes.string,
  isDetecting: PropTypes.bool,
  detectedLanguage: PropTypes.object,
  visible: PropTypes.bool,
  locale: PropTypes.string,
  isDisabled: PropTypes.bool,
  tags: PropTypes.array,
};

export default injectIntl(OriginalSection);
